import React from 'react';
import { useTranslation } from 'react-i18next';
import { Notice } from '@origo/origo-ui';
import { WarningIcon, InfoIcon } from '@phosphor-icons/react';
import type { ErstatningArt } from '@origo/planregister';
import styles from './ReplacementNotice.module.css';

export interface ReplacementPlan {
    nasjonalArealplanId: string;
    /** Mangler til navnet er hentet, eller når planen ikke finnes i registeret. */
    plannavn?: string;
    art: ErstatningArt;
}

const ART_LABEL_KEY: Record<ErstatningArt, string> = {
    erstattet: 'planValidity.replacedBy',
    delvisErstattet: 'planValidity.partlyReplacedBy',
};

interface ReplacementNoticeProps {
    /** Planstatus er Opphevet, Utgått/erstattet eller Overstyrt. */
    ikkeGjeldende: boolean;
    /** Planene som erstatter denne, fra planforholdene 8 og 10. */
    replacements: ReplacementPlan[];
    onOpenPlan: (napId: string) => void;
}

/**
 * Varsel i plandetaljen om at planen ikke gjelder og/eller er erstattet, med
 * lenker til erstattende planer (AK 6.6). Rendres ikke når ingen av delene gjelder.
 */
const ReplacementNotice: React.FC<ReplacementNoticeProps> = ({ ikkeGjeldende, replacements, onOpenPlan }) => {
    const { t } = useTranslation();
    if (!ikkeGjeldende && replacements.length === 0) return null;

    return (
        <div className={styles.wrapper}>
            <Notice
                variant={ikkeGjeldende ? 'warning' : 'info'}
                icon={ikkeGjeldende
                    ? <WarningIcon size={20} weight="duotone" />
                    : <InfoIcon size={20} weight="duotone" />}
            >
                {ikkeGjeldende && <p className={styles.title}>{t('planValidity.noticeTitle')}</p>}
                {replacements.length > 0 && (
                    <ul className={styles.list}>
                        {replacements.map(plan => (
                            <li key={plan.nasjonalArealplanId}>
                                {t(ART_LABEL_KEY[plan.art])}{' '}
                                {/* Uten navn er planen ikke hentet (ennå) — vis ID-en uten lenke. */}
                                {plan.plannavn ? (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.link}
                                            onClick={() => onOpenPlan(plan.nasjonalArealplanId)}
                                        >
                                            {plan.plannavn}
                                        </button>{' '}
                                        <span className={styles.id}>({plan.nasjonalArealplanId})</span>
                                    </>
                                ) : (
                                    <span className={styles.id}>{plan.nasjonalArealplanId}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Notice>
        </div>
    );
};

export default ReplacementNotice;
